import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Circle, Float, Image } from '@chakra-ui/react'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'

import { TakePhotoStepImageData } from 'components/partials/individualOrder/IndividualOrderItemInfo/components/pickupReturnFlow/steps/TakePhotosStep'
import { LeidButtonWhiteOutlined } from 'components/ui/Buttons/Button'
import XIcon from 'public/icons/x.svg'

import 'swiper/css'

import NavIcon from './NavIcon'

type Props = {
  images: TakePhotoStepImageData[]
  handleImageDelete: (index: number) => void
}

const ImagesSlider = ({ images, handleImageDelete }: Props): JSX.Element => {
  const sliderRef = useRef<SwiperRef>(null)

  const [isPrevActive, setIsPrevActive] = useState(false)
  const [isNextActive, setIsNextActive] = useState(false)

  const imagesOnSlide = 3

  useEffect(() => {
    setNavigationButtons()
    if (sliderRef.current) {
      let lastIndex = images.length - imagesOnSlide
      lastIndex = lastIndex > 0 ? lastIndex : 0
      sliderRef.current.swiper.slideTo(lastIndex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length])

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) {
      return
    }
    sliderRef.current.swiper.slidePrev()
  }, [])

  const handleNext = useCallback(() => {
    if (!sliderRef.current) {
      return
    }
    sliderRef.current.swiper.slideNext()
  }, [])

  const handleSliderChange = () => {
    setNavigationButtons()
  }

  const isPrevButtonActive = () => {
    if (!sliderRef.current) {
      return false
    }
    if (images.length < imagesOnSlide + 1) {
      return false
    }

    const activeIndex = sliderRef?.current?.swiper.activeIndex
    return activeIndex !== 0
  }

  const isNextButtonActive = () => {
    if (!sliderRef.current) {
      return false
    }
    if (images.length < imagesOnSlide + 1) {
      return false
    }

    const activeIndex = sliderRef?.current?.swiper.activeIndex
    return activeIndex !== images.length - imagesOnSlide
  }

  const setNavigationButtons = () => {
    const prevButtonIsActive = isPrevButtonActive()
    setIsPrevActive(prevButtonIsActive)

    const nextButtonIsActive = isNextButtonActive()
    setIsNextActive(nextButtonIsActive)
  }

  const getImagesOnSlide = () => {
    if (images.length > imagesOnSlide - 1) {
      return imagesOnSlide
    }

    if (images.length === 0) {
      return 'auto'
    }

    return images.length
  }

  return (
    <>
      <Box
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          paddingBottom: 1,
        }}
      >
        <Box
          display={{ base: 'none', lg: 'block' }}
          style={{
            marginRight: 2,
          }}
        >
          <NavIcon type="prev" onClick={handlePrev} disabled={!isPrevActive} />
        </Box>
        <Swiper
          loop={false}
          ref={sliderRef}
          spaceBetween={10}
          slidesPerView={getImagesOnSlide()}
          onSlideChange={handleSliderChange}
          style={{ paddingTop: '20px', paddingBottom: '10px', paddingRight: '10px' }}
        >
          {images.map((image, index) => {
            return (
              <SwiperSlide key={index}>
                <>
                  <Box key={`box_${index}`} display="flex" justifyContent="center" position="relative" height="100%">
                    {image.sequenceIndex !== undefined && (
                      <Float placement="bottom-end">
                        <Circle size="5" bg="green.a200" color="white">
                          {image.sequenceIndex + 1}
                        </Circle>
                      </Float>
                    )}

                    <Float style={{ zIndex: 9999 }} offsetX="4">
                      <LeidButtonWhiteOutlined key={`button_${index}`} onClick={() => handleImageDelete(index)}>
                        <XIcon key={`x_${index}`} />
                      </LeidButtonWhiteOutlined>
                    </Float>

                    <Image
                      key={`image_${index}`}
                      width="50px"
                      objectFit={'cover'}
                      alt="Asset photo"
                      src={URL.createObjectURL(image.imageBlob) || '/images/placeholder.png'}
                    />
                  </Box>
                </>
              </SwiperSlide>
            )
          })}
        </Swiper>
        <Box
          display={{ base: 'none', lg: 'block' }}
          style={{
            marginLeft: 2,
          }}
        >
          <NavIcon type="next" onClick={handleNext} disabled={!isNextActive} />
        </Box>
      </Box>

      <Box
        display={{ base: 'flex', lg: 'none' }}
        style={{
          gap: 6,
          marginTop: 3,
          alignItems: 'center',
        }}
      >
        <NavIcon type="prev" onClick={handlePrev} disabled={!isPrevActive} />
        <NavIcon type="next" onClick={handleNext} disabled={!isNextActive} />
      </Box>
    </>
  )
}

export default ImagesSlider
